import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { NAV_LINKS, PHONE } from "../../data/site";
import { scrollToId } from "../../hooks/useLenis";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (target) => {
        setOpen(false);
        scrollToId(target);
    };

    return (
        <motion.header
            data-testid="site-navbar"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
            }`}
        >
            <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-12">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("hero")}
                    className="font-display text-lg font-semibold tracking-tight text-white"
                >
                    GAN<span className="text-gold">.</span>
                </button>

                <div className="hidden items-center gap-9 lg:flex">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.target}
                            data-testid={`nav-link-${l.target}`}
                            onClick={() => go(l.target)}
                            className="group relative font-body text-sm text-neutral-300 transition-colors duration-300 hover:text-white"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </div>

                <a
                    data-testid="nav-call-btn"
                    href={`tel:${PHONE.raw}`}
                    className="hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-body text-sm font-semibold text-black transition-transform duration-300 hover:scale-[0.97] lg:inline-flex"
                >
                    <Phone size={15} strokeWidth={2} />
                    Programează-te
                </a>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="text-white lg:hidden"
                    aria-label="Meniu"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-6">
                            {NAV_LINKS.map((l) => (
                                <button
                                    key={l.target}
                                    data-testid={`mobile-nav-${l.target}`}
                                    onClick={() => go(l.target)}
                                    className="border-b border-white/5 py-3 text-left font-display text-xl text-neutral-200"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <a
                                data-testid="mobile-call-btn"
                                href={`tel:${PHONE.raw}`}
                                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-body font-semibold text-black"
                            >
                                <Phone size={16} /> Programează-te telefonic
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
